import { useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

import { useCountries } from "../../catalogs/countries";
import { PatientResults } from "../../components/patient-search-palette";
import { resolveEnumLabel } from "../../components/ui-kit/fields/utils";
import {
  MAX_SEARCH_RESULTS,
  SearchPalette,
} from "../../components/ui-kit/search-palette";
import { monitoringTypes, patientStatuses } from "../../constants/enum-to-text";
import { PatientSearchModel } from "./patient-search-model";

/**
 * The patient list's own search, as it was before the redesign moved search
 * into the top bar (`PatientSearch`). Shown only in the legacy layout, which
 * keeps its searches beside the page title.
 */
export const LegacyPatientSearch = observer(function LegacyPatientSearch({
  model,
}: {
  model: PatientSearchModel;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const countries = useCountries();

  function handleSelect(patientId: number) {
    model.close();
    void navigate(`/patient/${patientId}`);
  }

  // The whole patient list is held in memory, so an empty query would render
  // every patient there is. Capped like every other palette.
  const matches = model.filteredPatients;

  return (
    <SearchPalette
      open={model.isOpen}
      onOpenChange={(open) => {
        if (open) void model.open();
        else model.close();
      }}
      query={model.query}
      onQueryChange={(v) => {
        model.query = v;
      }}
      triggerLabel={t`Search patients...`}
      inputPlaceholder={t`Search by name or ID...`}
      title={t`Patient Search`}
      description={t`Search patients by name or ID`}
      loading={model.loading}
      emptyMessage={t`No patients found.`}
    >
      <PatientResults
        results={matches.slice(0, MAX_SEARCH_RESULTS).map((patient) => {
          const regionSchema =
            patient.region != null
              ? countries.getRegionSchema(patient.country, patient.region)
              : undefined;
          return {
            id: patient.patient_id,
            name: patient.full_name,
            subtitle: [
              patient.monitoring_type != null &&
                resolveEnumLabel(monitoringTypes[patient.monitoring_type]),
              patient.patient_status != null &&
                resolveEnumLabel(patientStatuses[patient.patient_status]),
              [patient.country, regionSchema?.region_name]
                .filter(Boolean)
                .join(" / "),
            ],
          };
        })}
        matchCount={matches.length}
        onSelect={handleSelect}
      />
    </SearchPalette>
  );
});
